import { useMemo, useRef, useState } from 'react'
import {
  BarChart3,
  Eye,
  Image as ImageIcon,
  MousePointerClick,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Field, Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import {
  useAdminPromos,
  useCreatePromo,
  useDeletePromo,
  useUpdatePromo,
  useUploadPromoImage,
  type AdminPromo,
  type PromoAccent,
  type PromoInput,
  type PromoKind,
  type PromoSlot,
  type PromoStatus,
} from '@/features/promo/api'
import { ApiError } from '@/lib/api'
import { formatRupiah } from '@/lib/format'

const slotLabels: Record<PromoSlot, string> = {
  beranda_slideshow: 'Slideshow beranda',
  beranda_banner: 'Banner beranda',
  katalog_banner: 'Banner katalog kelas',
  dasbor_banner: 'Banner dasbor pengguna',
}

const slotKeterangan: Record<PromoSlot, string> = {
  beranda_slideshow: 'Petak besar paling atas beranda. Semua slide aktif diputar bergantian.',
  beranda_banner: 'Strip mendatar di tengah beranda. Hanya satu yang tampil.',
  katalog_banner: 'Di atas daftar kelas, dilihat pengunjung yang sedang mencari kelas.',
  dasbor_banner: 'Di dalam aplikasi, hanya untuk pengguna yang sudah masuk.',
}

const statusLabels: Record<PromoStatus, string> = {
  draft: 'Draf',
  active: 'Tayang',
  paused: 'Dijeda',
}

const accentLabels: Record<PromoAccent, string> = {
  hijau: 'Hijau',
  ungu: 'Ungu',
  kuning: 'Kuning',
  gelap: 'Gelap',
}

const urutanSlot: PromoSlot[] = [
  'beranda_slideshow',
  'beranda_banner',
  'katalog_banner',
  'dasbor_banner',
]

const kosong: PromoInput = {
  kind: 'slide',
  slot: 'beranda_slideshow',
  title: '',
  subtitle: '',
  image_url: '',
  cta_label: '',
  target_url: '',
  accent: 'hijau',
  advertiser: '',
  price: 0,
  sort_order: 0,
  status: 'draft',
  starts_at: '',
  ends_at: '',
}

export function PromoManagerPage() {
  const { data, isLoading } = useAdminPromos()
  const promos = useMemo(() => data?.promos ?? [], [data])

  const [terbuka, setTerbuka] = useState(false)
  const [disunting, setDisunting] = useState<AdminPromo | null>(null)

  const ringkasan = useMemo(() => {
    const tayang = promos.reduce((jumlah, p) => jumlah + p.impressions, 0)
    const klik = promos.reduce((jumlah, p) => jumlah + p.clicks, 0)
    return {
      aktif: promos.filter((p) => p.live).length,
      tayang,
      klik,
      // Rasio dihitung dari totalnya, bukan dari rata-rata rasio tiap
      // tayangan: merata-ratakan persentase memberi bobot sama kepada iklan
      // yang muncul sepuluh kali dan yang muncul sepuluh ribu kali.
      rasio: tayang === 0 ? 0 : (klik / tayang) * 100,
      pemasukan: promos.filter((p) => p.kind === 'ad').reduce((jumlah, p) => jumlah + p.price, 0),
    }
  }, [promos])

  const buka = (promo: AdminPromo | null) => {
    setDisunting(promo)
    setTerbuka(true)
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Slideshow &amp; iklan
          </h1>
          <p className="mt-1 text-muted">
            Atur apa yang dilihat lebih dulu oleh pengunjung beranda, dan jual ruangnya kepada pemasang.
          </p>
        </div>
        <Button onClick={() => buka(null)}>
          <Plus className="size-4" />
          Tambah tayangan
        </Button>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KartuAngka label="Sedang tayang" nilai={String(ringkasan.aktif)} icon={<Eye className="size-4" />} />
        <KartuAngka label="Total tayang" nilai={ringkasan.tayang.toLocaleString('id-ID')} icon={<BarChart3 className="size-4" />} />
        <KartuAngka
          label="Total klik"
          nilai={`${ringkasan.klik.toLocaleString('id-ID')} · ${ringkasan.rasio.toFixed(1)}%`}
          icon={<MousePointerClick className="size-4" />}
        />
        <KartuAngka label="Nilai pemasangan" nilai={formatRupiah(ringkasan.pemasukan)} icon={<ImageIcon className="size-4" />} />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : promos.length === 0 ? (
        <Card>
          <EmptyState
            illustration="box"
            icon={<ImageIcon className="size-6" />}
            title="Belum ada tayangan"
            description="Slide pertama akan langsung muncul di paling atas beranda begitu statusnya diubah menjadi Tayang."
            action={<Button onClick={() => buka(null)}>Buat slide pertama</Button>}
          />
        </Card>
      ) : (
        urutanSlot
          .map((slot) => ({ slot, isi: promos.filter((p) => p.slot === slot) }))
          .filter((grup) => grup.isi.length > 0)
          .map((grup) => (
            <section key={grup.slot} className="space-y-3">
              <div>
                <h2 className="text-lg font-extrabold tracking-tight text-ink">{slotLabels[grup.slot]}</h2>
                <p className="text-sm text-muted">{slotKeterangan[grup.slot]}</p>
              </div>

              <div className="space-y-3">
                {grup.isi.map((promo) => (
                  <BarisPromo key={promo.id} promo={promo} onSunting={() => buka(promo)} />
                ))}
              </div>
            </section>
          ))
      )}

      <FormulirPromo
        key={disunting?.id ?? 'baru'}
        open={terbuka}
        promo={disunting}
        onClose={() => setTerbuka(false)}
      />
    </div>
  )
}

function KartuAngka({ label, nilai, icon }: { label: string; nilai: string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardBody className="space-y-1">
        <p className="mono-label flex items-center gap-1.5 text-[11px] text-subtle">
          {icon}
          {label}
        </p>
        <p className="text-xl font-extrabold tabular-nums text-ink">{nilai}</p>
      </CardBody>
    </Card>
  )
}

function BarisPromo({ promo, onSunting }: { promo: AdminPromo; onSunting: () => void }) {
  const hapus = useDeletePromo()
  const simpan = useUpdatePromo()
  const toast = useToast()
  const [konfirmasi, setKonfirmasi] = useState(false)

  // Tombol jeda mengirim seluruh isi tayangan kembali, bukan hanya statusnya:
  // endpoint penyuntingan memang menerima bentuk utuh, dan mengirim sebagian
  // akan mengosongkan sisanya.
  const ubahStatus = () => {
    const status: PromoStatus = promo.status === 'active' ? 'paused' : 'active'
    simpan.mutate(
      { ...keInput(promo), id: promo.id, status },
      {
        onSuccess: () =>
          toast.success(status === 'active' ? 'Tayangan diaktifkan' : 'Tayangan dijeda'),
        onError: (error) => toast.error('Gagal mengubah status', pesan(error)),
      },
    )
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="h-20 w-full shrink-0 overflow-hidden rounded-xl border border-line bg-sunken sm:w-32">
          {promo.image_url ? (
            <img src={promo.image_url} alt="" className="size-full object-cover" />
          ) : (
            <div className="grid size-full place-items-center text-subtle">
              <ImageIcon className="size-5" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={promo.live ? 'success' : promo.status === 'paused' ? 'warning' : 'neutral'}>
              {promo.live ? 'Tayang' : statusLabels[promo.status]}
            </Badge>
            {promo.kind === 'ad' ? <Badge tone="info">Iklan berbayar</Badge> : null}
            <span className="mono-label text-[10px] text-subtle">Urutan {promo.sort_order}</span>
          </div>

          <p className="mt-1.5 truncate font-bold text-ink">{promo.title}</p>
          {promo.subtitle ? <p className="truncate text-sm text-muted">{promo.subtitle}</p> : null}

          <p className="mono-label mt-1.5 text-[11px] text-subtle">
            {promo.impressions.toLocaleString('id-ID')} tayang · {promo.clicks.toLocaleString('id-ID')} klik ·{' '}
            {promo.click_rate.toFixed(1)}%
            {promo.kind === 'ad' && promo.advertiser ? ` · ${promo.advertiser}` : ''}
            {promo.kind === 'ad' && promo.price > 0 ? ` · ${formatRupiah(promo.price)}` : ''}
          </p>

          {promo.starts_at || promo.ends_at ? (
            <p className="mt-1 text-xs text-subtle">
              {promo.starts_at ? `Mulai ${tanggalSingkat(promo.starts_at)}` : 'Tanpa awal'} —{' '}
              {promo.ends_at ? `berakhir ${tanggalSingkat(promo.ends_at)}` : 'tanpa akhir'}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" variant="outline" onClick={ubahStatus} disabled={simpan.isPending}>
            {promo.status === 'active' ? 'Jeda' : 'Tayangkan'}
          </Button>
          <Button size="sm" variant="outline" onClick={onSunting} aria-label="Sunting tayangan">
            <Pencil className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setKonfirmasi(true)}
            aria-label="Hapus tayangan"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </CardBody>

      <Dialog
        open={konfirmasi}
        onClose={() => setKonfirmasi(false)}
        title="Hapus tayangan ini?"
        description="Gambarnya ikut terhapus dan hitungan tayang serta kliknya tidak dapat dikembalikan."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setKonfirmasi(false)}>
              Batal
            </Button>
            <Button
              variant="danger"
              disabled={hapus.isPending}
              onClick={() =>
                hapus.mutate(promo.id, {
                  onSuccess: () => {
                    setKonfirmasi(false)
                    toast.success('Tayangan dihapus')
                  },
                  onError: (error) => toast.error('Gagal menghapus', pesan(error)),
                })
              }
            >
              Hapus
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">{promo.title}</p>
      </Dialog>
    </Card>
  )
}

function FormulirPromo({
  open,
  promo,
  onClose,
}: {
  open: boolean
  promo: AdminPromo | null
  onClose: () => void
}) {
  const [nilai, setNilai] = useState<PromoInput>(promo ? keInput(promo) : kosong)
  const berkas = useRef<HTMLInputElement>(null)

  const buat = useCreatePromo()
  const ubah = useUpdatePromo()
  const unggah = useUploadPromoImage()
  const toast = useToast()

  const [galat, setGalat] = useState('')
  const menyimpan = buat.isPending || ubah.isPending

  const set = <K extends keyof PromoInput>(kunci: K, isi: PromoInput[K]) =>
    setNilai((kini) => ({ ...kini, [kunci]: isi }))

  const kirim = () => {
    setGalat('')

    const muatan: PromoInput = {
      ...nilai,
      // Waktu dikirim dalam RFC3339 lengkap dengan zona. Kolom
      // datetime-local memberi waktu tanpa zona, dan mengirimnya apa adanya
      // membuat jadwal tayang meleset tujuh jam bagi kami di Indonesia.
      starts_at: keRFC(nilai.starts_at),
      ends_at: keRFC(nilai.ends_at),
    }

    const selesai = {
      onSuccess: () => {
        toast.success(promo ? 'Tayangan diperbarui' : 'Tayangan dibuat')
        onClose()
      },
      onError: (error: unknown) => setGalat(pesan(error)),
    }

    if (promo) ubah.mutate({ ...muatan, id: promo.id }, selesai)
    else buat.mutate(muatan, selesai)
  }

  const pilihGambar = (file: File | undefined) => {
    if (!file) return
    unggah.mutate(file, {
      onSuccess: (hasil) => set('image_url', hasil.image_url),
      onError: (error) => toast.error('Gambar gagal diunggah', pesan(error)),
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={promo ? 'Sunting tayangan' : 'Tayangan baru'}
      description="Slide beranda wajib bergambar; banner boleh berupa teks berlatar warna."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button onClick={kirim} disabled={menyimpan}>
            {menyimpan ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {galat}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Jenis"
            value={nilai.kind}
            onChange={(e) => set('kind', e.target.value as PromoKind)}
            hint={nilai.kind === 'ad' ? 'Ruang yang dibeli pemasang; ditandai "Bersponsor".' : 'Promosi milik skava.id sendiri.'}
          >
            <option value="slide">Promosi sendiri</option>
            <option value="ad">Iklan berbayar</option>
          </Select>

          <Select label="Letak" value={nilai.slot} onChange={(e) => set('slot', e.target.value as PromoSlot)}>
            {urutanSlot.map((slot) => (
              <option key={slot} value={slot}>
                {slotLabels[slot]}
              </option>
            ))}
          </Select>
        </div>

        <p className="rounded-xl bg-sunken px-3 py-2 text-sm text-muted">{slotKeterangan[nilai.slot]}</p>

        <Input
          label="Judul"
          required
          value={nilai.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Belajar bikin aplikasi bareng AI"
        />

        <Textarea
          label="Keterangan"
          rows={2}
          value={nilai.subtitle}
          onChange={(e) => set('subtitle', e.target.value)}
          placeholder="Kelas pendampingan personal, langsung praktik dari nol."
        />

        <Field
          label="Gambar"
          hint="JPG, PNG, atau WebP, maksimal 3 MB. Ukuran ideal 1600×900."
          required={nilai.slot === 'beranda_slideshow'}
        >
          <div className="flex items-center gap-3">
            <div className="h-20 w-32 shrink-0 overflow-hidden rounded-xl border border-line bg-sunken">
              {nilai.image_url ? (
                <img src={nilai.image_url} alt="" className="size-full object-cover" />
              ) : (
                <div className="grid size-full place-items-center text-subtle">
                  <ImageIcon className="size-5" />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <input
                ref={berkas}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => pilihGambar(e.target.files?.[0])}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => berkas.current?.click()}
                disabled={unggah.isPending}
              >
                <Upload className="size-4" />
                {unggah.isPending ? 'Mengunggah…' : 'Pilih gambar'}
              </Button>
              {nilai.image_url ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => set('image_url', '')}>
                  Hapus gambar
                </Button>
              ) : null}
            </div>
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Teks tombol"
            value={nilai.cta_label}
            onChange={(e) => set('cta_label', e.target.value)}
            placeholder="Lihat kelasnya"
          />
          <Input
            label="Tautan tujuan"
            value={nilai.target_url}
            onChange={(e) => set('target_url', e.target.value)}
            placeholder="/kelas atau https://…"
            hint="Diawali / untuk halaman di dalam skava.id."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Warna"
            value={nilai.accent}
            onChange={(e) => set('accent', e.target.value as PromoAccent)}
          >
            {(Object.keys(accentLabels) as PromoAccent[]).map((warna) => (
              <option key={warna} value={warna}>
                {accentLabels[warna]}
              </option>
            ))}
          </Select>

          <Input
            label="Urutan"
            type="number"
            min={0}
            value={nilai.sort_order}
            onChange={(e) => set('sort_order', Number(e.target.value))}
            hint="Kecil tampil lebih dulu."
          />

          <Select
            label="Status"
            value={nilai.status}
            onChange={(e) => set('status', e.target.value as PromoStatus)}
          >
            {(Object.keys(statusLabels) as PromoStatus[]).map((status) => (
              <option key={status} value={status}>
                {statusLabels[status]}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Mulai tayang"
            type="datetime-local"
            value={nilai.starts_at ?? ''}
            onChange={(e) => set('starts_at', e.target.value)}
            hint="Kosongkan untuk langsung tayang."
          />
          <Input
            label="Berhenti tayang"
            type="datetime-local"
            value={nilai.ends_at ?? ''}
            onChange={(e) => set('ends_at', e.target.value)}
            hint="Kosongkan untuk tayang terus."
          />
        </div>

        {nilai.kind === 'ad' ? (
          <div className="grid gap-4 rounded-xl bg-sunken p-4 sm:grid-cols-2">
            <Input
              label="Nama pemasang"
              value={nilai.advertiser}
              onChange={(e) => set('advertiser', e.target.value)}
              placeholder="Nando Studio"
              hint="Ditampilkan di sebelah label Bersponsor."
            />
            <Input
              label="Nilai pemasangan"
              type="number"
              min={0}
              value={nilai.price}
              onChange={(e) => set('price', Number(e.target.value))}
              prefix="Rp"
              hint="Catatan internal; tidak pernah terlihat pengunjung."
            />
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}

/** keInput mengubah tayangan dari peladen menjadi isi formulir. */
function keInput(promo: AdminPromo): PromoInput {
  return {
    kind: promo.kind,
    slot: promo.slot,
    title: promo.title,
    subtitle: promo.subtitle ?? '',
    image_url: promo.image_url ?? '',
    cta_label: promo.cta_label ?? '',
    target_url: promo.target_url ?? '',
    accent: promo.accent,
    advertiser: promo.advertiser ?? '',
    price: promo.price,
    sort_order: promo.sort_order,
    status: promo.status,
    starts_at: keLokal(promo.starts_at),
    ends_at: keLokal(promo.ends_at),
  }
}

/**
 * keLokal mengubah waktu RFC3339 menjadi bentuk yang diterima
 * <input type="datetime-local">, yaitu waktu setempat tanpa zona.
 */
function keLokal(iso?: string): string {
  if (!iso) return ''

  const waktu = new Date(iso)
  if (Number.isNaN(waktu.getTime())) return ''

  const geser = waktu.getTime() - waktu.getTimezoneOffset() * 60_000
  return new Date(geser).toISOString().slice(0, 16)
}

/** keRFC mengembalikan waktu setempat menjadi RFC3339 bersama zonanya. */
function keRFC(lokal?: string): string | undefined {
  if (!lokal) return undefined

  const waktu = new Date(lokal)
  if (Number.isNaN(waktu.getTime())) return undefined
  return waktu.toISOString()
}

function tanggalSingkat(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

function pesan(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Terjadi kesalahan yang tidak terduga.'
}
